import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { Calendar, MapPin, ExternalLink, Activity, Trophy, Globe, Radio, RefreshCw } from 'lucide-react';
import { useLanguage } from './LanguageContext';
// Isolated countdown to prevent parent re-renders
function CountdownTimer() {
  const {
    t
  } = useLanguage();
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    mins: 0,
    secs: 0
  });
  useEffect(() => {
    const target = new Date('2026-06-11T15:00:00-04:00').getTime();
    const update = () => {
      const now = Date.now();
      const diff = Math.max(target - now, 0);
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor(diff / (1000 * 60 * 60) % 24),
        mins: Math.floor(diff / (1000 * 60) % 60),
        secs: Math.floor(diff / 1000 % 60)
      });
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true
  }} className="bg-[#0a0a0a] rounded-xl p-5 md:p-6 border-l-4 border-england-gold text-center" data-id="element-212">
      <p className="text-england-gold font-oswald uppercase tracking-widest text-xs md:text-sm mb-3" data-id="element-213">
        {t('fanHq.countdown')}
      </p>
      <div className="flex items-center justify-center gap-3 md:gap-5" data-id="element-214">
        {[{
        value: timeLeft.days,
        label: t('fanHq.days')
      }, {
        value: timeLeft.hours,
        label: t('fanHq.hrs')
      }, {
        value: timeLeft.mins,
        label: t('fanHq.min')
      }, {
        value: timeLeft.secs,
        label: t('fanHq.sec')
      }].map((unit, i) => <div key={i} className="flex flex-col items-center" data-id="element-215">
            <div className="bg-england-navy/50 border border-england-navy/30 rounded-lg w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mb-1" data-id="element-216">
              <span className="font-oswald text-xl md:text-3xl font-bold text-white" data-id="element-217">
                {String(unit.value).padStart(2, '0')}
              </span>
            </div>
            <span className="font-oswald text-[9px] md:text-[10px] uppercase tracking-widest text-gray-400" data-id="element-218">
              {unit.label}
            </span>
          </div>)}
      </div>
      <p className="text-gray-500 text-[10px] md:text-xs mt-3 font-oswald uppercase tracking-wider" data-id="element-219">
        England vs TBD — MetLife Stadium, NJ
      </p>
    </motion.div>;
}
export function MatchCenter() {
  const [iframeKey, setIframeKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const {
    t
  } = useLanguage();
  // Lazy load iframe when standings panel scrolls into view
  const standingsRef = useRef(null);
  const standingsInView = useInView(standingsRef, {
    once: true,
    margin: '200px'
  });
  const [iframeLoaded, setIframeLoaded] = useState(false);
  useEffect(() => {
    if (standingsInView) {
      setIframeLoaded(true);
    }
  }, [standingsInView]);
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    setIframeKey(prev => prev + 1);
    setTimeout(() => setIsRefreshing(false), 1500);
  }, []);
  // Auto-refresh iframe every 5 minutes (only when loaded)
  useEffect(() => {
    if (!iframeLoaded) return;
    const interval = setInterval(() => {
      setIframeKey(prev => prev + 1);
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [iframeLoaded]);
  const matches = [{
    date: 'June 11, 2026',
    time: '3:00 PM ET',
    competition: 'World Cup 2026 - Group B',
    homeTeam: 'England',
    awayTeam: 'TBD',
    homeFlag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
    awayFlag: '❓',
    venue: 'MetLife Stadium, NJ',
    isNext: true
  }, {
    date: 'June 16, 2026',
    time: '12:00 PM ET',
    competition: 'World Cup 2026 - Group B',
    homeTeam: 'England',
    awayTeam: 'TBD',
    homeFlag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
    awayFlag: '❓',
    venue: 'Lincoln Financial Field, PA',
    isNext: false
  }, {
    date: 'June 20, 2026',
    time: '3:00 PM ET',
    competition: 'World Cup 2026 - Group B',
    homeTeam: 'TBD',
    awayTeam: 'England',
    homeFlag: '❓',
    awayFlag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
    venue: 'Gillette Stadium, MA',
    isNext: false
  }];
  return <section id="matches" className="py-12 md:py-24 bg-[#111] relative border-y border-white/5" data-id="element-220">
      <div className="container mx-auto px-6 md:px-12" data-id="element-221">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center max-w-3xl mx-auto mb-12 md:mb-16" data-id="element-222">
          <h2 className="text-england-red font-oswald uppercase tracking-widest text-sm font-bold mb-3" data-id="element-223">
            {t('matches.label')}
          </h2>
          <h3 className="text-3xl md:text-5xl font-oswald font-bold uppercase mb-6" data-id="element-224">
            {t('matches.title1')}{' '}
            <span className="text-england-gold" data-id="element-225">{t('matches.title2')}</span>
          </h3>
          <p className="text-gray-400 text-sm md:text-base" data-id="element-226">
            {t('matches.desc')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12" data-id="element-227">
          {/* Live Standings */}
          <motion.div ref={standingsRef} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="lg:col-span-1 bg-[#0a0a0a] rounded-xl border border-white/10 h-fit overflow-hidden" data-id="element-228">
            <div className="p-6 pb-4" data-id="element-229">
              <div className="flex items-center justify-between mb-4" data-id="element-230">
                <h4 className="font-oswald text-xl uppercase font-bold text-england-gold flex items-center gap-2" data-id="element-231">
                  <Trophy size={18} data-id="element-232" /> {t('matches.standings')}
                </h4>
                <span className="flex items-center gap-1.5 bg-england-red/20 border border-england-red/40 text-england-red text-[10px] font-oswald uppercase tracking-widest px-2 py-1 rounded-full" data-id="element-233">
                  <Radio size={10} className="animate-pulse" data-id="element-234" />{' '}
                  {t('matches.live')}
                </span>
              </div>
            </div>

            {/* Embedded FlashScore Standings — lazy loaded */}
            <div className="w-full aspect-[4/5] bg-[#111] relative" data-id="element-235">
              {iframeLoaded ? <iframe key={iframeKey} src="https://www.flashscoreusa.com/soccer/world/world-cup/standings/SbLsX4y7/standings/" title="World Cup Group Live Standings" className="w-full h-full border-0" loading="lazy" data-id="element-236" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-237">
                  <div className="w-8 h-8 border-2 border-england-gold/30 border-t-england-gold rounded-full animate-spin" data-id="element-238" />
                </div>}
            </div>

            {/* Refresh controls + fallback link */}
            <div className="p-4 border-t border-white/5" data-id="element-239">
              <div className="flex items-center justify-between mb-3" data-id="element-240">
                <span className="text-[10px] text-gray-500 font-oswald uppercase tracking-widest" data-id="element-241">
                  {t('matches.autoRefresh')}
                </span>
                <button onClick={handleRefresh} className="flex items-center gap-1.5 text-[10px] text-england-gold font-oswald uppercase tracking-widest hover:text-white transition-colors min-h-[44px] px-2" data-id="element-242">
                  <RefreshCw size={12} className={isRefreshing ? 'animate-spin' : ''} data-id="element-243" />
                  {t('matches.refreshNow')}
                </button>
              </div>
              <a href="https://www.flashscoreusa.com/soccer/world/world-cup/standings/SbLsX4y7/standings/" target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-4 py-3 rounded-lg font-oswald uppercase tracking-wider text-xs font-bold transition-all duration-300 min-h-[44px]" data-id="element-244">
                <Radio size={14} className="text-england-red" data-id="element-245" />
                {t('matches.viewStandings')}
              </a>
            </div>
          </motion.div>

          {/* Matches List */}
          <div className="lg:col-span-2 space-y-4" data-id="element-246">
            <CountdownTimer data-id="element-247" />
            {matches.map((match, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className={`bg-[#0a0a0a] rounded-xl p-5 md:p-6 flex flex-col md:flex-row items-center justify-between gap-6 border-l-4 ${match.isNext ? 'border-england-red shadow-[0_0_20px_rgba(207,8,31,0.1)]' : 'border-white/10'} hover:border-england-gold transition-colors duration-300`} data-id="element-248">
                {/* Date & Info */}
                <div className="flex-1 text-center md:text-left w-full" data-id="element-249">
                  {match.isNext && <span className="inline-block bg-england-red text-white text-[10px] font-oswald uppercase tracking-widest px-2 py-1 rounded mb-2" data-id="element-250">
                      {t('matches.nextMatch')}
                    </span>}
                  <p className="text-england-gold text-xs font-oswald uppercase tracking-widest mb-1" data-id="element-251">
                    {match.competition}
                  </p>
                  <div className="flex items-center justify-center md:justify-start gap-2 text-sm text-gray-400 mb-2" data-id="element-252">
                    <span className="flex items-center gap-1" data-id="element-253">
                      <Calendar size={14} data-id="element-254" /> {match.date}
                    </span>
                    <span className="w-1 h-1 bg-gray-600 rounded-full" data-id="element-255"></span>
                    <span data-id="element-256">{match.time}</span>
                  </div>
                  <p className="flex items-center justify-center md:justify-start gap-1 text-xs text-gray-500" data-id="element-257">
                    <MapPin size={12} data-id="element-258" /> {match.venue}
                  </p>
                </div>

                {/* Teams */}
                <div className="flex-1 flex items-center justify-center gap-4 md:gap-6 w-full" data-id="element-259">
                  <div className="text-right w-20 md:w-24" data-id="element-260">
                    <div className="text-2xl md:text-3xl mb-1" data-id="element-261">
                      {match.homeFlag}
                    </div>
                    <div className="font-oswald font-bold uppercase text-xs md:text-sm truncate" data-id="element-262">
                      {match.homeTeam}
                    </div>
                  </div>
                  <div className="bg-white/5 px-3 py-1.5 rounded-lg font-oswald text-gray-400 text-xs md:text-sm shrink-0" data-id="element-263">
                    VS
                  </div>
                  <div className="text-left w-20 md:w-24" data-id="element-264">
                    <div className="text-2xl md:text-3xl mb-1" data-id="element-265">
                      {match.awayFlag}
                    </div>
                    <div className="font-oswald font-bold uppercase text-xs md:text-sm truncate" data-id="element-266">
                      {match.awayTeam}
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* External Links */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto" data-id="element-267">
          <a href="https://www.espn.com/soccer/team/fixtures/_/id/448/eng" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-[#cc0000]/10 hover:bg-[#cc0000]/20 border border-[#cc0000]/30 text-white px-6 py-4 rounded-xl font-oswald uppercase tracking-wider text-sm font-bold transition-all duration-300 min-h-[44px]" data-id="element-268">
            <Activity size={16} className="text-[#cc0000]" data-id="element-269" />
            {t('matches.espn')}
          </a>
          <a href="https://www.sofascore.com/football/team/england/4713" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-[#326295]/10 hover:bg-[#326295]/20 border border-[#326295]/30 text-white px-6 py-4 rounded-xl font-oswald uppercase tracking-wider text-sm font-bold transition-all duration-300 min-h-[44px]" data-id="element-270">
            <Globe size={16} className="text-[#326295]" data-id="element-271" />
            {t('matches.sofascore')}
          </a>
          <a href="https://www.flashscoreusa.com/soccer/world/world-cup/#/SbLsX4y7/standings/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-6 py-4 rounded-xl font-oswald uppercase tracking-wider text-sm font-bold transition-all duration-300 min-h-[44px]" data-id="element-272">
            <ExternalLink size={16} className="text-gray-400" data-id="element-273" />
            {t('matches.flashscore')}
          </a>
          <a href="https://www.foxsports.com/soccer/england-men-team-stats" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-england-navy/10 hover:bg-england-navy/20 border border-england-navy/30 text-white px-6 py-4 rounded-xl font-oswald uppercase tracking-wider text-sm font-bold transition-all duration-300 min-h-[44px]" data-id="element-274">
            <ExternalLink size={16} className="text-england-navy" data-id="element-275" />
            {t('matches.foxsports')}
          </a>
        </motion.div>
      </div>
    </section>;
}